import { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react";
import { submitForm } from "./utils/api";
import { isValidEmail, isValidUKPhone, GetOnboarderDOBRange, formatDate, isValidOnboarderDOBRange } from "./utils/validation";
import { nationalities } from "../../data/nationalities";

const OnboardingForm = ({ clientKey, formId }) => {
     const [fieldErrors, setFieldErrors] = useState({});

     const { minODOB } = GetOnboarderDOBRange();
     const formattedMinDOB = formatDate(minODOB);

     const [search, setSearch] = useState("");

     const [fullName, setName] = useState("");
     const [prefName, setPrefName] = useState("");
     const [dob, setDob] = useState("");
     const [addressLine1, setAddressLine1] = useState("");
     const [addressLine2, setAddressLine2] = useState("");
     const [city, setCity] = useState("");
     const [postcode, setPostcode] = useState("");

     const address = [addressLine1, addressLine2, city, postcode]
          .filter(Boolean)
          .join("\n");

     const [phone, setPhone] = useState("");
     const [email, setEmail] = useState("");

     const [eName, setEName] = useState("");
     const [eRelation, setERelation] = useState("");
     const [ePhone, setEPhone] = useState("");

     const [nationality, setNationality] = useState("");
     const [rtw, setRtw] = useState("");
     const [passport, setPassport] = useState("");
     const [visa, setVisa] = useState("");
     const [expiry, setExpiry] = useState("");
     const [shareCode, setShareCode] = useState("");

     const [ni, setNI] = useState("");
     const [p45, setP45] = useState("");
     const [docP45, setDocP45] = useState(null); // this will now store S3 URL

     const [bankHName, setBankHName] = useState("");
     const [bankName, setBankName] = useState("");
     const [bankSort, setBankSort] = useState("");
     const [bankNo, setBankNo] = useState("");

     const [pension, setPension] = useState("");
     const [pensionName, setPensionName] = useState("");

     const [medical, setMedical] = useState("");
     const [adjustments, setAdjustments] = useState("");

     const [consent, setConsent] = useState(false);

     const [loading, setLoading] = useState(false);
     const [success, setSuccess] = useState(null);
     const [error, setError] = useState(null);

     const [open, setOpen] = useState(false);
     const wrapperRef = useRef(null);

     useEffect(() => {
          const handleClickOutside = (e) => {
               if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                    setOpen(false);
               }
          };

          document.addEventListener("mousedown", handleClickOutside);
          return () => document.removeEventListener("mousedown", handleClickOutside);
     }, []);



     useEffect(() => {
          const ukNationalities = ["British", "Irish"];

          if (!nationality) {
               setRtw("");
               return;
          }

          if (ukNationalities.includes(nationality)) {
               setRtw("yes");
          } else {
               setRtw("no");
          }
     }, [nationality]);

     useEffect(() => {
          if (rtw === "yes") {
               setVisa("");
               setExpiry("");
          } else if (rtw === "no") {
               setPassport("");
               setShareCode("");
          }
     }, [rtw]);

     const uploadP45ToS3 = async (file) => {
          const extension = file.name.split(".").pop();
          const nameAsFileName = fullName.trim().replace(/\s+/g, "") + "." + extension;

          const res = await fetch("https://7t0xfgk6y1.execute-api.eu-west-2.amazonaws.com/generateUploadUrl", {
               method: "POST",
               headers: { "Content-Type": "application/json" },
               body: JSON.stringify({
                    clientId: clientKey,
                    formId: formId,
                    reason: "p45",
                    fileName: nameAsFileName,
                    fileType: file.type,
                    password: "citoslearners2511.23",
               }),
          });

          if (!res.ok) throw new Error("Failed to get upload URL");

          const { uploadUrl, fileUrl } = await res.json();

          const uploadRes = await fetch(uploadUrl, {
               method: "PUT",
               headers: {
                    "Content-Type": file.type,
               },
               body: file,
          });

          if (!uploadRes.ok) throw new Error("S3 upload failed");

          return fileUrl;
     };

     const validate = () => {
          const errors = {};
          const cleanEmail = email?.trim();

          if (cleanEmail && !isValidEmail(cleanEmail)) {
               errors.email = "Please enter a valid email address.";
          }

          if (phone && !isValidUKPhone(phone)) {
               errors.phone = "Please enter a valid UK phone number.";
          }

          if (!isValidOnboarderDOBRange(dob, minODOB)) {
               errors.dob = "You must be at least 18 years old to onboard.";
          }

          if (ePhone && !isValidUKPhone(ePhone)) {
               errors.ePhone = "Please enter a valid emergency contact number.";
          }

          const validNationality = nationalities.some(
               (n) => n.toLowerCase() === nationality?.toLowerCase()
          );

          if (!validNationality) {
               errors.nationality = "Please select a valid nationality.";
          }

          const niRegex = /^[A-CEGHJ-PR-TW-Z]{2}\d{6}[A-D]$/;
          if (ni && !niRegex.test(ni.toUpperCase())) {
               errors.ni = "Enter a valid NI number.";
          }

          const sortCodeRegex = /^\d{2}-\d{2}-\d{2}$/;
          const accountNumberRegex = /^\d{8}$/;

          if (bankSort && !sortCodeRegex.test(bankSort)) {
               errors.bankSort = "Enter a valid 6-digit sort code.";
          }

          if (bankNo && !accountNumberRegex.test(bankNo)) {
               errors.bankNo = "Enter a valid 8-digit account number.";
          }

          if (rtw === "yes" && !passport && !shareCode) {
               errors.rtw = "Provide passport number or share code.";
          }

          if (rtw === "no") {
               if (!visa) errors.visa = "Visa type is required.";
               if (!expiry) errors.expiry = "Visa expiry date is required.";
          }

          setFieldErrors(errors);
          return Object.keys(errors).length === 0;
     };

     /** Reset file if P45 option changes */
     useEffect(() => {
          if (p45 === "no") {
               setDocP45(null);
          }
     }, [p45]);

     const handleSubmit = async (e) => {
          e.preventDefault();

          if (loading) return;

          setLoading(true);
          setSuccess(null);
          setError(null);

          if (!validate()) {
               setError("Please fix the errors above");
               setLoading(false);
               return;
          }

          try {
               let p45Url = docP45;

               // if file exists, upload first
               if (docP45 && typeof docP45 !== "string") {
                    p45Url = await uploadP45ToS3(docP45);
               }

               const payload = {
                    client: clientKey,
                    formid: formId,

                    fullName,
                    prefName,
                    dob,
                    address,
                    phone,
                    email,

                    eName,
                    eRelation,
                    ePhone,

                    nationality,
                    rtw,
                    passport,
                    visa,
                    expiry,
                    shareCode,

                    ni,
                    p45Status: p45,
                    p45Document: p45Url,

                    bankHName,
                    bankName,
                    bankSort,
                    bankNo,

                    pension,
                    pensionName,

                    medical,
                    adjustments,
               };

               const response = await submitForm(payload);

               if (response.status === 200) {
                    setSuccess("Your details have been submitted successfully!");
               } else {
                    setError("Something went wrong, please try again.");
               }
          } catch (err) {
               console.log("Error status:", err?.response?.status);
               console.log("Error data:", err?.response?.data);
               setError("Error occurred. Please try again.");
          } finally {
               setLoading(false);
          }
     };

     const filtered = nationalities.filter((n) =>
          n.toLowerCase().includes(search.toLowerCase())
     );

     /** Sync manual nationality selection properly */
     useEffect(() => {
          if (!open && search) {
               const match = nationalities.find(
                    (n) => n.toLowerCase() === search.toLowerCase()
               );
               if (match) setNationality(match);
          }
     }, [open, search]);
     return (
          <div className="form-container">
               <form onSubmit={handleSubmit} className="form-inner">
                    <section className="sect1">
                         <h3>Personal Details</h3>

                         <div className="sect">
                              <label>Full Legal Name</label>
                              <div className="padder">
                                   <input
                                        value={fullName}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="enter your full legal name..."
                                        required
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Preferred Name</label>
                              <div className="padder">
                                   <input
                                        value={prefName}
                                        onChange={(e) => setPrefName(e.target.value)}
                                        placeholder="enter your preferred name..."
                                        required
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Date of Birth</label>
                              <div className="padder">
                                   <input
                                        type="date"
                                        value={dob}
                                        min={formattedMinDOB}
                                        onChange={(e) => setDob(e.target.value)}
                                        required
                                        placeholder="Select your DOB"
                                   />
                              </div>
                              {fieldErrors.dob && (
                                   <span className="error-text">{fieldErrors.dob}</span>
                              )}
                         </div>

                         <div className="sect">
                              <label>Address</label>

                              <div className="padder">
                                   <input
                                        value={addressLine1}
                                        onChange={(e) => setAddressLine1(e.target.value)}
                                        placeholder="address line 1..."
                                        required
                                   />
                              </div>

                              <div className="padder">
                                   <input
                                        value={addressLine2}
                                        onChange={(e) => setAddressLine2(e.target.value)}
                                        placeholder="address line 2..."
                                   />
                              </div>

                              <div className="padder">
                                   <input
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        placeholder="city..."
                                        required
                                   />
                              </div>

                              <div className="padder">
                                   <input
                                        value={postcode}
                                        onChange={(e) => setPostcode(e.target.value)}
                                        placeholder="postcode..."
                                        required
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Email</label>
                              <div className="padder">
                                   <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="enter your email..."
                                        required
                                   />
                              </div>
                              {fieldErrors.email && (
                                   <span className="error-text">{fieldErrors.email}</span>
                              )}
                         </div>

                         <div className="sect">
                              <label>Phone number</label>
                              <div className="padder">
                                   <input
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder="enter your phone number..."
                                        required
                                   />
                              </div>
                              {fieldErrors.phone && (
                                   <span className="error-text">{fieldErrors.phone}</span>
                              )}
                         </div>
                    </section>

                    <section className="sect2">
                         <h3>Emergency Contact</h3>

                         <div className="sect">
                              <label>Full Name</label>
                              <div className="padder">
                                   <input
                                        value={eName}
                                        onChange={(e) => setEName(e.target.value)}
                                        placeholder="enter emergency contact's full name..."
                                        required
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Relationship</label>
                              <div className="padder">
                                   <input
                                        value={eRelation}
                                        onChange={(e) => setERelation(e.target.value)}
                                        placeholder="enter relationship to your emergency contact..."
                                        required
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Phone number</label>
                              <div className="padder">
                                   <input
                                        value={ePhone}
                                        onChange={(e) => setEPhone(e.target.value)}
                                        placeholder="enter emergency contact's phone number..."
                                        required
                                   />
                              </div>
                              {fieldErrors.ePhone && (
                                   <span className="error-text">{fieldErrors.ePhone}</span>
                              )}
                         </div>
                    </section>

                    <section className="sect3">
                         <h3>Right to Work in the UK</h3>

                         <div className="sect">
                              <label>Nationality</label>
                              <div className="padder" ref={wrapperRef}>
                                   <input
                                        className="nationality"
                                        value={search}
                                        onChange={(e) => {
                                             setSearch(e.target.value);
                                             setNationality(""); // prevents invalid partial overwrite
                                             setOpen(true);
                                        }}
                                        onFocus={() => setOpen(true)}
                                        placeholder="search your nationality..."
                                   />

                                   {fieldErrors.nationality && (
                                        <span className="error-text">
                                             {fieldErrors.nationality}
                                        </span>
                                   )}

                                   {open && filtered.length > 0 && (
                                        <div className="dropdown">
                                             {filtered.map((f) => (
                                                  <div
                                                       key={f}
                                                       className="option"
                                                       onClick={() => {
                                                            setNationality(f);
                                                            setSearch(f);
                                                            setOpen(false);
                                                       }}
                                                  >
                                                       {f}
                                                  </div>
                                             ))}
                                        </div>
                                   )}
                              </div>
                         </div>

                         <div className="sect">
                              <label>Do you have the right to work in the UK?</label>
                              <div className="padder">
                                   <select value={rtw} onChange={(e) => setRtw(e.target.value)}>
                                        <option value="">Select an option</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                   </select>
                                   <span className="select-icon">
                                        <Icon icon="gridicons:dropdown" />
                                   </span>
                              </div>
                              {fieldErrors.rtw && (
                                   <span className="error-text">{fieldErrors.rtw}</span>
                              )}
                         </div>

                         {rtw === "yes" && (
                              <>
                                   <div className="sect">
                                        <label>Passport Number (if applicable)</label>
                                        <div className="padder">
                                             <input
                                                  type="text"
                                                  placeholder="enter your passport number..."
                                                  value={passport}
                                                  onChange={(e) => setPassport(e.target.value)}
                                             />
                                        </div>
                                   </div>

                                   <div className="sect">
                                        <label>Share Code (if applicable)</label>
                                        <div className="padder">
                                             <input
                                                  type="text"
                                                  placeholder="enter share code..."
                                                  value={shareCode}
                                                  onChange={(e) => setShareCode(e.target.value)}
                                             />
                                        </div>
                                   </div>
                              </>
                         )}

                         {rtw === "no" && (
                              <>
                                   <div className="sect">
                                        <label>Visa Type</label>
                                        <div className="padder">
                                             <input
                                                  type="text"
                                                  placeholder="enter visa type..."
                                                  value={visa}
                                                  onChange={(e) => setVisa(e.target.value)}
                                             />
                                        </div>
                                        {fieldErrors.visa && (
                                             <span className="error-text">
                                                  {fieldErrors.visa}
                                             </span>
                                        )}
                                   </div>

                                   <div className="sect">
                                        <label>Visa Expiry Date</label>
                                        <div className="padder">
                                             <input
                                                  type="date"
                                                  value={expiry}
                                                  onChange={(e) => setExpiry(e.target.value)}
                                                  placeholder="Select the expiry date"
                                             />
                                        </div>
                                        {fieldErrors.expiry && (
                                             <span className="error-text">
                                                  {fieldErrors.expiry}
                                             </span>
                                        )}
                                   </div>
                              </>
                         )}
                    </section>

                    <section className="sect4">
                         <h3>National Insurance & Tax</h3>

                         <div className="sect">
                              <label>National Insurance Number</label>
                              <div className="padder">
                                   <input
                                        type="text"
                                        placeholder="enter NI number (e.g. QQ123456C)..."
                                        value={ni}
                                        onChange={(e) =>
                                             setNI(e.target.value.toUpperCase())
                                        }
                                   />
                              </div>
                              {fieldErrors.ni && (
                                   <span className="error-text">{fieldErrors.ni}</span>
                              )}
                         </div>

                         <div className="sect">
                              <label>Do you have a P45 from your previous employer?</label>
                              <div className="padder">
                                   <select value={p45} onChange={(e) => setP45(e.target.value)}>
                                        <option value="">Select an option</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                   </select>
                                   <span className="select-icon">
                                        <Icon icon="gridicons:dropdown" />
                                   </span>
                              </div>
                         </div>

                         {p45 === "yes" && (
                              <div className="sect">
                                   <label>Upload your P45</label>
                                   <div className="padder">
                                        <input
                                             type="file"
                                             accept=".pdf,.jpg,.png"
                                             onChange={(e) =>
                                                  setDocP45(e.target.files[0])
                                             }
                                        />
                                   </div>
                              </div>
                         )}

                         {p45 === "no" && (
                              <div className="sect">
                                   <p className="info-text">
                                        You will need to complete the HMRC Starter Checklist.
                                        This will be provided separately.
                                   </p>
                              </div>
                         )}
                    </section>

                    <section className="sect5">
                         <h3>Bank Details</h3>

                         <div className="sect">
                              <label>Account Holder Name</label>
                              <div className="padder">
                                   <input
                                        type="text"
                                        value={bankHName}
                                        onChange={(e) =>
                                             setBankHName(e.target.value)
                                        }
                                        placeholder="enter account holder's full name..."
                                        required
                                   />
                              </div>
                              {fieldErrors.bankHName && (
                                   <span className="error-text">
                                        {fieldErrors.bankHName}
                                   </span>
                              )}
                         </div>

                         <div className="sect">
                              <label>Bank Name</label>
                              <div className="padder">
                                   <input
                                        type="text"
                                        value={bankName}
                                        onChange={(e) =>
                                             setBankName(e.target.value)
                                        }
                                        placeholder="enter the bank's name..."
                                        required
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Sort Code</label>
                              <div className="padder">
                                   <input
                                        type="text"
                                        value={bankSort}
                                        onChange={(e) => {
                                             let value = e.target.value.replace(/\D/g, ""); // remove non-digits

                                             if (value.length > 6) value = value.slice(0, 6); // limit to 6 digits

                                             if (value.length > 4) {
                                                  value = value.replace(/(\d{2})(\d{2})(\d{0,2})/, "$1-$2-$3");
                                             } else if (value.length > 2) {
                                                  value = value.replace(/(\d{2})(\d{0,2})/, "$1-$2");
                                             }

                                             setBankSort(value);
                                        }}
                                        placeholder="enter the sort code..."
                                        required
                                   />
                              </div>
                              {fieldErrors.bankSort && (
                                   <span className="error-text">
                                        {fieldErrors.bankSort}
                                   </span>
                              )}
                         </div>

                         <div className="sect">
                              <label>Account Number</label>
                              <div className="padder">
                                   <input
                                        type="text"
                                        value={bankNo}
                                        onChange={(e) =>
                                             setBankNo(e.target.value)
                                        }
                                        placeholder="enter the account number..."
                                        required
                                   />
                              </div>
                              {fieldErrors.bankNo && (
                                   <span className="error-text">
                                        {fieldErrors.bankNo}
                                   </span>
                              )}
                         </div>
                    </section>

                    <section className="sect6">
                         <h3>Pension (Auto-Enrolment)</h3>

                         <div className="sect">
                              <label>Are you currently enrolled in a workplace pension?</label>
                              <div className="padder">
                                   <select
                                        value={pension}
                                        onChange={(e) => setPension(e.target.value)}
                                   >
                                        <option value="">Select an option</option>
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                   </select>
                                   <span className="select-icon">
                                        <Icon icon="gridicons:dropdown" />
                                   </span>
                              </div>
                         </div>

                         {pension === "yes" && (
                              <div className="sect">
                                   <label>Provider Name</label>
                                   <div className="padder">
                                        <input
                                             type="text"
                                             value={pensionName}
                                             onChange={(e) =>
                                                  setPensionName(e.target.value)
                                             }
                                             placeholder="enter your pension provider's name..."
                                        />
                                   </div>
                              </div>
                         )}
                    </section>

                    <section className="sect7">
                         <h3>Health & Accessibility (Optional)</h3>

                         <p className="info-text">
                              This information helps us support you at work. You may choose
                              not to disclose.
                         </p>

                         <div className="sect">
                              <label>Do you have any medical conditions we should be aware of?</label>
                              <div className="padder">
                                   <textarea
                                        value={medical}
                                        onChange={(e) => setMedical(e.target.value)}
                                        rows={3}
                                        placeholder="enter any medical conditions here..."
                                   />
                              </div>
                         </div>

                         <div className="sect">
                              <label>Do you require any workplace adjustments?</label>
                              <div className="padder">
                                   <textarea
                                        value={adjustments}
                                        onChange={(e) =>
                                             setAdjustments(e.target.value)
                                        }
                                        rows={3}
                                        placeholder="enter any workplace adjustments you rewuire here..."
                                   />
                              </div>
                         </div>
                    </section>

                    <section className="sect8">
                         <h3>Data Protection & Consent</h3>

                         <div className="sect">
                              <div className="padder checkbox">
                                   <input
                                        type="checkbox"
                                        id="consent"
                                        checked={consent}
                                        onChange={(e) =>
                                             setConsent(e.target.checked)
                                        }
                                        required
                                   />

                                   <label htmlFor="consent">
                                        I confirm that the information provided is accurate
                                        and complete. I understand that my data will be
                                        processed in accordance with company policies and UK
                                        data protection laws.
                                   </label>
                              </div>
                         </div>
                    </section>

                    {/* <button disabled={loading}> */}
                    <button disabled={loading || !consent}>
                         {loading ? "Sending..." : "Submit"}
                    </button>

                    {success && (
                         <div className="success-message">{success}</div>
                    )}
                    {error && <div className="error-message">{error}</div>}
               </form>
          </div>
     );
}

export default OnboardingForm;